import { SparklesIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import { generateAutoMappings, hasAutofillSuggestions } from "./autofillUtils";
import { AutofillButtonProps } from "./types";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";

const AutofillButton = observer(
  ({
    variables,
    sources,
    sourcesV2,
    onAutofill,
    disabled = false,
    isLoading = false,
    mappableCount = 0,
    readonly = false,
  }: AutofillButtonProps) => {
    const autofillResult = (() => {
      if (isLoading || disabled || variables.length === 0) {
        return null;
      }
      return generateAutoMappings(variables, sources, sourcesV2);
    })();

    const hasSuggestions = (() => {
      if (isLoading || variables.length === 0) {
        return false;
      }
      return hasAutofillSuggestions(variables, sources, sourcesV2);
    })();

    const handleClick = () => {
      if (!autofillResult || disabled || isLoading) {
        return;
      }
      onAutofill(autofillResult.mappings);
    };

    const isButtonDisabled =
      disabled || isLoading || !hasSuggestions || variables.length === 0 || readonly;

    const tooltipContent = (() => {
      if (isLoading) {
        return "Loading...";
      }
      if (readonly) {
        return "Autofill is disabled in readonly mode";
      }
      if (variables.length === 0) {
        return "All variables are already mapped";
      }
      if (disabled && !hasSuggestions) {
        return "No matching sources found for unmapped variables";
      }
      if (disabled) {
        return "Autofill is not available";
      }
      if (mappableCount > 0) {
        return `Auto-map ${mappableCount} unmapped variable${mappableCount === 1 ? "" : "s"}`;
      }
      if (autofillResult) {
        const { totalMapped, totalPossible } = autofillResult;
        return `Auto-map ${totalMapped} of ${totalPossible} variables`;
      }
      return "Automatically map variables to matching sources";
    })();

    return (
      <BasicTooltip content={tooltipContent} shouldShow>
        <span className={isButtonDisabled ? "inline-block" : ""}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={isButtonDisabled}
            className="flex items-center gap-1.5"
          >
            <SparklesIcon className="h-4 w-4" />
            Autofill
            {(mappableCount > 0 || (autofillResult && autofillResult.totalMapped > 0)) && (
              <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                {mappableCount > 0 ? mappableCount : autofillResult?.totalMapped || 0}
              </span>
            )}
          </Button>
        </span>
      </BasicTooltip>
    );
  },
);

export default AutofillButton;
